import React, { useState } from 'react';
import axios from 'axios';
import EventStatusSelector from './EventStatusSelector';

export default function EventList({ events, onSelect, onDelete, onEdit }) {
    // SOLA-65-followup: menu "Azioni" unico per evento (evita troppi pulsanti in riga)
    const [openMenuId, setOpenMenuId] = useState(null);

    const statusLabels = {
        0: { label: 'Creato', color: 'secondary' },
        1: { label: 'Attivo', color: 'success' },
        2: { label: 'Pausa', color: 'warning' },
        3: { label: 'Iniziato', color: 'info' },
        4: { label: 'Gioco', color: 'primary' },
        5: { label: 'Terminato', color: 'dark' },
        6: { label: 'Risultati Pubblicati', color: 'success' },
        7: { label: 'Inattivo', color: 'light text-muted' },
        8: { label: 'Cancellato', color: 'danger' }
    };

    const handleStatusChange = async (ev, newStatus) => {
        const currentStatus = ev.status || 0;
        const nextStatus = parseInt(newStatus);

        // Prevent downgrading if game started (>=4)
        if (currentStatus >= 4 && nextStatus < currentStatus) {
            window.Swal.fire({
                title: 'Azione non consentita',
                text: 'Non è possibile retrocedere lo stato una volta iniziato il gioco.',
                icon: 'error',
                customClass: { popup: 'swal2-glass' }
            });
            return;
        }

        try {
            await axios.put(`/speeddate/api/events/${ev.id}`, { ...ev, status: nextStatus });
            window.Swal.fire({
                title: 'Stato Aggiornato',
                icon: 'success',
                timer: 1000,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
            if (onDelete) onDelete(); // Trigger a refresh
        } catch (err) {
            window.Swal.fire('Errore', "Impossibile aggiornare lo stato", 'error');
        }
    };

    const deleteEvent = async (id, e) => {
        e.stopPropagation();
        window.Swal.fire({
            title: 'Eliminare l\'evento?',
            text: "Tutti i dati associati a questo evento andranno persi.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`/speeddate/api/events/${id}`);
                    window.Swal.fire({
                        title: 'Eliminato!',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                        toast: true,
                        position: 'top-end'
                    });
                    if (onDelete) onDelete();
                } catch (err) {
                    window.Swal.fire('Errore', "Impossibile eliminare l'evento.", 'error');
                }
            }
        });
    };

    const archiveEvent = async (id, e) => {
        e.stopPropagation();
        window.Swal.fire({
            title: 'Archiviare l\'evento?',
            text: "L'evento non comparirà più nella lista attiva ma i dati verranno conservati.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#6366f1',
            cancelButtonColor: '#aaa',
            confirmButtonText: 'Sì, archivia',
            cancelButtonText: 'Annulla',
            customClass: { popup: 'swal2-glass' }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.post(`/speeddate/api/events/${id}/archive`);
                    window.Swal.fire({
                        title: 'Archiviato!',
                        text: 'L\'evento è stato spostato nell\'archivio.',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                        toast: true,
                        position: 'top-end',
                        customClass: { popup: 'swal2-glass' }
                    });
                    if (onDelete) onDelete();
                } catch (err) {
                    window.Swal.fire('Errore', "Impossibile archiviare l'evento.", 'error');
                }
            }
        });
    };

    // SOLA-65 — Duplica evento: chiede conferma + nuova data e orario, poi copia
    // tutto l'evento (descrizione, città, orario, copertina, biglietti, tag) sul
    // backend. La duplicazione invia anche le email di notifica ai sottoscrittori.
    const duplicateEvent = async (ev, e) => {
        e.stopPropagation();
        const minDate = new Date().toISOString().split('T')[0];
        const srcStart = ev.startTime ? String(ev.startTime).slice(0, 5) : '';
        const srcEnd = ev.endTime ? String(ev.endTime).slice(0, 5) : '';
        window.Swal.fire({
            title: 'Duplicare l\'evento?',
            html:
                `Verrà copiato tutto: descrizione, città, orario, copertina, biglietti e tag di<br><strong>${ev.name}</strong>.<br>Il nuovo evento sarà creato in una nuova data e con l'orario indicato.` +
                `<div class="mt-3 text-start" style="max-width:300px;margin:0 auto">` +
                `<label class="fw-bold small d-block mb-1">Nuova data</label>` +
                `<input type="date" id="dup-date" class="swal2-input" min="${minDate}" max="2099-12-31" style="width:100%">` +
                `<label class="fw-bold small d-block mb-1 mt-2">Orario inizio</label>` +
                `<input type="time" id="dup-start" class="swal2-input" step="900" ${srcStart ? `value="${srcStart}"` : ''} style="width:100%">` +
                `<label class="fw-bold small d-block mb-1 mt-2">Orario fine (opzionale)</label>` +
                `<input type="time" id="dup-end" class="swal2-input" step="900" ${srcEnd ? `value="${srcEnd}"` : ''} style="width:100%">` +
                `</div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#6366f1',
            cancelButtonColor: '#aaa',
            confirmButtonText: 'Sì, duplica',
            cancelButtonText: 'Annulla',
            customClass: { popup: 'swal2-glass' },
            preConfirm: () => {
                const date = document.getElementById('dup-date').value;
                const startTime = document.getElementById('dup-start').value;
                const endTime = document.getElementById('dup-end').value;
                if (!date) {
                    window.Swal.showValidationMessage('Seleziona una data per il nuovo evento!');
                    return false;
                }
                if (!startTime) {
                    window.Swal.showValidationMessage('Seleziona l\'orario di inizio!');
                    return false;
                }
                return { date, startTime, endTime };
            }
        }).then(async (result) => {
            if (result.isConfirmed && result.value && result.value.date) {
                try {
                    const { date, startTime, endTime } = result.value;
                    const res = await axios.post(`/speeddate/api/events/${ev.id}/duplicate`, { date, startTime, endTime });
                    const newDate = date.split('-').reverse().join('/');
                    window.Swal.fire({
                        title: 'Evento duplicato!',
                        html: `Nuovo evento <strong>#${res.data.id}</strong> per il <strong>${newDate}</strong> alle <strong>${startTime}</strong>.<br><span class="small">Iscritti alle notifiche avvisati via email.</span>`,
                        icon: 'success',
                        timer: 2600,
                        showConfirmButton: false,
                        toast: true,
                        position: 'top-end',
                        customClass: { popup: 'swal2-glass' }
                    });
                    if (onDelete) onDelete(); // Refresh lista
                } catch (err) {
                    window.Swal.fire('Errore', err.response?.data?.error || 'Impossibile duplicare l\'evento.', 'error');
                }
            }
        });
    };

    const handleDownloadPdf = async (ev, e) => {
        e.stopPropagation();
        try {
            const response = await axios.get(`/speeddate/api/events/${ev.id}/participants/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const safeName = ev.name.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40);
            link.setAttribute('download', `partecipanti_${safeName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            window.Swal.fire({
                title: 'Errore',
                text: 'Impossibile generare il PDF',
                icon: 'error',
                customClass: { popup: 'swal2-glass' }
            });
        }
    };

    return (
        <div className="table-responsive">
            <table className="table table-hover align-middle">
                <thead className="bg-light">
                    <tr>
                        <th className="small text-uppercase fw-bold text-muted border-0">Evento</th>
                        <th className="small text-uppercase fw-bold text-muted border-0">Data & Luogo</th>
                        <th className="small text-uppercase fw-bold text-muted border-0 text-center">Restrizioni</th>
                        <th className="small text-uppercase fw-bold text-muted border-0 text-center">Stato</th>
                        <th className="small text-uppercase fw-bold text-muted border-0 text-center">Iscritti</th>
                        <th className="small text-uppercase fw-bold text-muted border-0 text-end">Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    {events.length === 0 ? (
                        <tr>
                            <td colSpan="6" className="text-center py-5">
                                <div className="text-muted">
                                    <i className="fas fa-calendar-times fa-3x mb-3 opacity-25"></i>
                                    <p className="mb-0">Nessun evento presente in archivio</p>
                                </div>
                            </td>
                        </tr>
                    ) : events.map(ev => {
                        const st = statusLabels[ev.status || 0];
                        const isPast = new Date(ev.date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
                        return (
                            <tr key={ev.id} className={`cursor-pointer ${isPast ? 'table-secondary' : ''}`} onClick={() => onSelect(ev)} style={{ cursor: 'pointer' }}>
                                <td>
                                    <div className="d-flex align-items-center">
                                        <div className={`bg-${st.color.split(' ')[0]} bg-opacity-10 text-${st.color.split(' ')[0]} rounded p-2 me-3 d-none d-md-block`}>
                                            <i className="fas fa-calendar-star fa-lg"></i>
                                        </div>
                                        <div>
                                            <span className="fw-bold d-block text-dark">{ev.name}</span>
                                            <span className="text-muted small">ID: #{ev.id}</span>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className="d-block small"><i className="fas fa-map-marker-alt text-muted me-2"></i>{ev.city ? <strong className="text-primary me-1">{ev.city.name}</strong> : ''}{ev.location}</span>
                                    <span className="d-block small"><i className="fas fa-clock text-muted me-2"></i>{new Date(ev.date).toLocaleDateString('it-IT')}</span>
                                </td>
                                <td className="text-center">
                                    <EventStatusSelector
                                        event={ev}
                                        onRefresh={onDelete}
                                        compact
                                    />
                                </td>
                                <td className="text-center">
                                    <span className={`badge rounded-pill bg-${st.color.split(' ')[0]} bg-opacity-10 text-${st.color.split(' ')[0]} fw-bold px-3 py-2`} style={{ minWidth: '100px' }}>
                                        {st.label}
                                    </span>
                                </td>
                                <td className="text-center">
                                    <div className="d-flex flex-column align-items-center gap-1">
                                        <span className={`badge rounded-pill ${ev.participants?.length > 0 ? 'bg-primary' : 'bg-light text-muted'}`}>
                                            {ev.participants?.length || 0} totali
                                        </span>
                                        <div className="d-flex gap-1">
                                            <span className="badge rounded-pill bg-info bg-opacity-10 text-info" style={{ fontSize: '10px' }}>
                                                <i className="fas fa-mars me-1"></i>{(ev.participants || []).filter(p => p.sex === 'male').length}
                                            </span>
                                            <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger" style={{ fontSize: '10px' }}>
                                                <i className="fas fa-venus me-1"></i>{(ev.participants || []).filter(p => p.sex === 'female').length}
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                {/* SOLA-65-followup: la colonna azioni NON deve aprire l'evento (stopPropagation sul td) */}
                                <td className="text-end" onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                                    {/* SOLA-65-followup: azioni unificate in un menu dropdown unico */}
                                    <div className="position-relative">
                                        <button className="btn btn-sm btn-primary py-2 shadow-sm" onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === ev.id ? null : ev.id); }} title="Azioni">
                                            <i className="fas fa-bars me-1"></i> Azioni <i className="fas fa-chevron-down ms-1 small"></i>
                                        </button>
                                        {openMenuId === ev.id && (
                                            <>
                                                {/* overlay per chiudere cliccando fuori dal menu */}
                                                <div className="position-fixed d-block" style={{ inset: 0, zIndex: 1040, cursor: 'default' }} onClick={() => setOpenMenuId(null)}></div>
                                                <div className="dropdown-menu show shadow border-0 rounded-3" style={{ position: 'absolute', right: 0, top: '100%', display: 'block', zIndex: 1050, minWidth: '220px', marginTop: '4px' }}>
                                                    <span className="dropdown-header text-muted small px-3">#{ev.id} — {ev.name}</span>
                                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onSelect(ev); }}>
                                                        <i className="fas fa-arrow-right me-2 text-primary"></i>Apri
                                                    </button>
                                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); handleDownloadPdf(ev, e); }}>
                                                        <i className="fas fa-download me-2 text-success"></i>Scarica lista partecipanti
                                                    </button>
                                                    <>
                                                        <div className="dropdown-divider"></div>
                                                        <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onEdit(ev); }}>
                                                            <i className="fas fa-edit me-2 text-primary"></i>Modifica
                                                        </button>
                                                        <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); duplicateEvent(ev, e); }}>
                                                            <i className="fas fa-copy me-2 text-secondary"></i>Duplica
                                                        </button>
                                                    </>
                                                    {ev.status >= 6 && (ev.participants || []).some(p => p.MatchEmailSent) && (
                                                        <>
                                                            <div className="dropdown-divider"></div>
                                                            <button className="dropdown-item text-danger" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); archiveEvent(ev.id, e); }}>
                                                                <i className="fas fa-archive me-2"></i>Archivia
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
